import type { StateCreator } from 'zustand';

import {
  addReissuedMedicationToPrescriptionPad,
  advancePrescriptionPadStep,
  confirmFormulationDispenseLimits,
  reloadPrescriptionAssitant,
  resetPrescriptionHistory,
  resetPrescriptionPad,
  revertFormulationDispenseLimits,
  setFormulationDispenseLimit,
  setIsCancellingPrescription,
  setIsPostingPrescription,
  setShowPrescribeStateError,
  updateDevicePadItems,
  updateMedicationPadItems,
  updatePrescriptionHistoryItemsToCancel,
  updatePrescriptionHistoryItemsToReissue,
  updatePrescriptionHistoryPreviousItems,
  updatePrescriptionPadDispenseNote,
  updatePrescriptionPadReviewAction,
  updatePrescriptionPadStep,
  updateShowCancellation,
} from './actions';
import { removeMedicationPadItemById } from './actions/removeMedicationPadItemById';
import { updateMedicationPadItemAttribute } from './actions/updateMedicationPadItemAttribute';
import { updateMedicationPadItemById } from './actions/updateMedicationPadItemById';
import { updatePrescriptionPadRules } from './actions/updatePrescriptionPadRules';
import {
  initialPrescriptionHistoryState,
  initialPrescriptionPadRules,
  initialPrescriptionPadState,
  initialPrescriptionSubmitState,
} from './constants';
import { isPrescriptionInProgress, isPrescriptionPadInSync, isValidPrescriptionPad } from './selectors';
import type { PrescriptionAssistantSliceType } from './types';

export const prescriptionAssistantSlice: StateCreator<
  PrescriptionAssistantSliceType,
  [['zustand/immer', never], ['zustand/devtools', never]],
  [],
  PrescriptionAssistantSliceType
> = (set, get, store) => ({
  // State
  prescriptionAssistant: {
    prescriptionHistory: initialPrescriptionHistoryState,
    prescriptionPad: initialPrescriptionPadState,
    prescriptionPadRules: initialPrescriptionPadRules,
    prescriptionSubmitState: initialPrescriptionSubmitState,
  },
  // Selectors/Computed State
  isValidPrescriptionPad: () => isValidPrescriptionPad(get),
  isPrescriptionPadInSync: () => isPrescriptionPadInSync(get),
  isPrescriptionInProgress: () => isPrescriptionInProgress(get),

  // Actions
  updateMedicationPadItems: (medicationPadItems) => updateMedicationPadItems(set, get, store)(medicationPadItems),
  removeMedicationPadItemById: (medicationPadItemId) =>
    removeMedicationPadItemById(set, get, store)(medicationPadItemId),
  updateMedicationPadItemById: (updatedMedicationPadItem) =>
    updateMedicationPadItemById(set, get, store)(updatedMedicationPadItem),
  updateMedicationPadItemAttribute: (medicationPadItemId, attribute, value) =>
    updateMedicationPadItemAttribute(set, get, store)(medicationPadItemId, attribute, value),
  addReissuedMedicationToPrescriptionPad: (availableMedications, reissuedMedication) =>
    addReissuedMedicationToPrescriptionPad(set, get, store)(availableMedications, reissuedMedication),
  updatePrescriptionPadRules: (rules) => updatePrescriptionPadRules(set, get, store)(rules),
  updateDevicePadItems: (devicePadItems) => updateDevicePadItems(set, get, store)(devicePadItems),
  updatePrescriptionPadReviewAction: (action) => updatePrescriptionPadReviewAction(set, get, store)(action),
  updatePrescriptionPadStep: (step) => updatePrescriptionPadStep(set, get, store)(step),
  updatePrescriptionHistoryItemsToReissue: (itemsToReissue) =>
    updatePrescriptionHistoryItemsToReissue(set, get, store)(itemsToReissue),
  updatePrescriptionHistoryItemsToCancel: (itemsToCancel) =>
    updatePrescriptionHistoryItemsToCancel(set, get, store)(itemsToCancel),
  updatePrescriptionHistoryPreviousItems: (previousItems) =>
    updatePrescriptionHistoryPreviousItems(set, get, store)(previousItems),
  updateShowCancellation: (show) => updateShowCancellation(set, get, store)(show),
  updatePrescriptionPadDispenseNote: (dispenseNote) => updatePrescriptionPadDispenseNote(set, get, store)(dispenseNote),
  setFormulationDispenseLimit: (formulation, limit) => setFormulationDispenseLimit(set, get, store)(formulation, limit),
  confirmFormulationDispenseLimits: (formulations) => confirmFormulationDispenseLimits(set, get, store)(formulations),
  revertFormulationDispenseLimits: (formulations) => revertFormulationDispenseLimits(set, get, store)(formulations),
  resetPrescriptionPad: () => resetPrescriptionPad(set, get, store)(),
  advancePrescriptionPadStep: () => advancePrescriptionPadStep(set, get, store)(),
  resetPrescriptionHistory: () => resetPrescriptionHistory(set, get, store)(),
  reloadPrescriptionAssitant: () => reloadPrescriptionAssitant(set, get, store)(),
  setIsPostingPrescription: (value) => setIsPostingPrescription(set, get, store)(value),
  setIsCancellingPrescription: (value) => setIsCancellingPrescription(set, get, store)(value),
  setShowPrescribeStateError: (value) => setShowPrescribeStateError(set, get, store)(value),
});
